// Detalle de formulario: métricas, Información general (URL + código), Vista previa,
// Funnel / Envíos y panel de Características.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Code2, Download, ExternalLink, Layers, Link2, type LucideIcon, Pencil, Save } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Loading, Select, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { FormPreview } from "./FormPreview.tsx";
import { FormSettingsPanel } from "./FormSettingsPanel.tsx";
import { CopyBox, KIND_LABEL, Metric, Tabs, Toggle, downloadCsv, pct, type FormSettings, type Links, type MktForm, useMktOptions } from "./shared.tsx";

type Tab = "general" | "preview" | "envios";
interface Submission {
  id: string; createdAt: string; status: string; contactCreated: boolean; accountCreated: boolean; contactId: string | null; accountId: string | null;
  contactName: string | null; contactEmail: string | null; utm: Record<string, string> | null;
  data: { summary?: Array<{ label: string; value: string }> };
}

export function FormDetail(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const admin = Boolean(useMe().data?.admin);
  const opts = useMktOptions();
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get("tab") as Tab) || "general";
  const filter = sp.get("filter") || "all";
  const setTab = (t: Tab): void => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n, { replace: true }); };
  const setFilter = (f: string): void => { const n = new URLSearchParams(sp); n.set("tab", "envios"); n.set("filter", f); setSp(n, { replace: true }); };

  const q = useQuery<{ form: MktForm; links: Links; pendingConfirm: number }>({ queryKey: ["mkt-form", id], queryFn: () => api.get(`/mkt/forms/${id}`) });
  const [settings, setSettings] = useState<FormSettings>({});
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (q.data) { setSettings(q.data.form.settings ?? {}); setDirty(false); } }, [q.data]);

  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");
  const refresh = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-form", id] }); void qc.invalidateQueries({ queryKey: ["mkt-forms"] }); };
  const publish = useMutation({
    mutationFn: (published: boolean) => api.post(`/mkt/forms/${id}/publish`, { published }),
    onSuccess: (_r, published) => { toast.success(published ? "Formulario publicado." : "Formulario despublicado (borrador)."); refresh(); }, onError: onErr,
  });
  const saveSettings = useMutation({
    mutationFn: () => api.patch(`/mkt/forms/${id}`, { settings }),
    onSuccess: () => { toast.success("Características guardadas."); setDirty(false); refresh(); }, onError: onErr,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar el formulario." onRetry={() => void q.refetch()} />;
  const { form, links } = q.data;
  const published = form.status === "published";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" className="btn-ghost btn-sm" onClick={() => nav("..")} aria-label="Volver"><ArrowLeft size={16} /></button>
          <h2 className="text-lg font-semibold text-ink truncate">{form.name}</h2>
          <Badge color={published ? "green" : "gray"}>{published ? "Publicado" : "Borrador"}</Badge>
          <span className="text-xs text-ink-muted">{KIND_LABEL[form.kind]}</span>
        </div>
        <div className="flex gap-2">
          {published && <a className="btn-secondary" href={links.pageUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /> Ver</a>}
          {admin && <Button onClick={() => nav("editar")}><Pencil size={16} /> Editar</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Metric label="Impresiones" value={form.impressions} />
        <Metric label="Conversiones" value={form.conversions} hint={`Tasa: ${pct(form.conversions, form.impressions)}`} onClick={() => setFilter("all")} active={tab === "envios" && filter === "all"} />
        <Metric label="Contactos creados" value={form.contactsCreated} onClick={() => setFilter("contacts")} active={tab === "envios" && filter === "contacts"} />
        <Metric label="Empresas creadas" value={form.accountsCreated} onClick={() => setFilter("accounts")} active={tab === "envios" && filter === "accounts"} />
        <Metric label="Pendientes de confirmar" value={q.data.pendingConfirm} hint="Doble opt-in" />
      </div>

      <div className="grid xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <Card className="p-0 overflow-hidden min-w-0">
          <div className="px-4 pt-2"><Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: "general", label: "Información general" }, { id: "preview", label: "Vista previa" }, { id: "envios", label: "Funnel / Envíos" }]} /></div>
          <div className="p-4">
            {tab === "general" && <GeneralTab form={form} links={links} canPublish={admin} publishing={publish.isPending} onPublish={() => publish.mutate(true)} />}
            {tab === "preview" && <PreviewTab form={form} />}
            {tab === "envios" && <SubmissionsTab form={form} filter={filter} setFilter={setFilter} />}
          </div>
        </Card>
        <Card className="p-4 space-y-4 min-w-0">
          <h3 className="sec-title">Características</h3>
          <Toggle checked={published} disabled={!admin || publish.isPending} onChange={(v) => publish.mutate(v)} label={published ? "Publicado" : "Borrador"}
            hint={published ? "Visible en las webs donde está incrustado." : "No se muestra ni acepta envíos."} />
          <FormSettingsPanel value={settings} onChange={(s) => { setSettings(s); setDirty(true); }} options={opts.data} disabled={!admin} />
          {admin && <Button variant="primary" className="w-full" disabled={!dirty || saveSettings.isPending} onClick={() => saveSettings.mutate()}><Save size={16} /> Guardar características</Button>}
        </Card>
      </div>
    </div>
  );
}

type EmbedMode = "embed" | "popup" | "link";
const EMBED_MODES: Array<{ id: EmbedMode; icon: LucideIcon; title: string; hint: string }> = [
  { id: "embed", icon: Code2, title: "En tu web", hint: "Aparece dentro de la página" },
  { id: "popup", icon: Layers, title: "Ventana emergente", hint: "Se abre sobre la página" },
  { id: "link", icon: Link2, title: "Enlace directo", hint: "Para correos o redes" },
];

function GeneralTab({ form, links, canPublish, publishing, onPublish }: { form: MktForm; links: Links; canPublish: boolean; publishing: boolean; onPublish: () => void }): JSX.Element {
  const [mode, setMode] = useState<EmbedMode>("embed");
  const [trigger, setTrigger] = useState<"delay" | "exit" | "scroll">(form.design.popup?.trigger ?? "delay");
  const [n, setN] = useState<number>(trigger === "scroll" ? (form.design.popup?.scroll ?? 50) : (form.design.popup?.delay ?? 5));
  const popupCode = `<script src="${links.scriptUrl}" data-mode="popup" data-trigger="${trigger}"${trigger === "delay" ? ` data-delay="${n}"` : trigger === "scroll" ? ` data-scroll="${n}"` : ""} async></script>`;
  const divCode = `<div data-tiger-form="${form.publicToken}"></div>\n${links.embedCode}`;
  return (
    <div className="space-y-4">
      {form.status !== "published" && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-warning-soft px-3 py-2">
          <span className="text-sm text-ink">Está en borrador: el enlace y el código no funcionan hasta publicarlo.</span>
          {canPublish && <Button small variant="primary" disabled={publishing} onClick={onPublish}>Publicar ahora</Button>}
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-2" role="tablist" aria-label="Cómo usar el formulario">
        {EMBED_MODES.map((m) => {
          const Icon = m.icon; const on = mode === m.id;
          return (
            <button key={m.id} type="button" role="tab" aria-selected={on} onClick={() => setMode(m.id)}
              className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors ${on ? "border-primary bg-primary-soft" : "border-line hover:bg-surface-hover"}`}>
              <Icon size={18} strokeWidth={1.75} className={`shrink-0 mt-0.5 ${on ? "text-primary" : "text-ink-muted"}`} />
              <span className="min-w-0"><span className={`block text-sm font-medium ${on ? "text-primary" : "text-ink"}`}>{m.title}</span><span className="block text-xs text-ink-muted">{m.hint}</span></span>
            </button>
          );
        })}
      </div>
      {mode === "embed" && (
        <div className="space-y-3">
          <CopyBox value={links.embedCode} />
          <p className="text-xs text-ink-muted">Pégalo donde quieras verlo. En WordPress o Elementor, usa un bloque «HTML personalizado». Si reemplazas un formulario de Clientify, borra antes su script.</p>
          <details className="text-xs text-ink-muted">
            <summary className="cursor-pointer hover:text-ink">¿No aparece en el lugar correcto? Usa esta versión con contenedor</summary>
            <div className="mt-2"><CopyBox value={divCode} /></div>
          </details>
        </div>
      )}
      {mode === "popup" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <Field label="Cuándo se abre">
              <Select value={trigger} onChange={(e) => { const t = e.target.value as "delay" | "exit" | "scroll"; setTrigger(t); setN(t === "scroll" ? 50 : 5); }}>
                <option value="delay">Tras unos segundos</option><option value="exit">Al intentar salir</option><option value="scroll">Al bajar por la página</option>
              </Select>
            </Field>
            {trigger !== "exit" && <Field label={trigger === "delay" ? "Segundos" : "% de la página"}><Input type="number" className="w-24" min={trigger === "delay" ? 0 : 1} max={trigger === "delay" ? 600 : 100} value={n} onChange={(e) => setN(Number(e.target.value) || 0)} /></Field>}
          </div>
          <CopyBox value={popupCode} />
          <p className="text-xs text-ink-muted">Pégalo una sola vez en la página, por ejemplo antes de cerrar el contenido.</p>
        </div>
      )}
      {mode === "link" && (
        <div className="space-y-3">
          <CopyBox value={links.pageUrl} />
          <a className="btn-secondary btn-sm w-fit" href={links.pageUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Abrir página</a>
        </div>
      )}
    </div>
  );
}

function PreviewTab({ form }: { form: MktForm }): JSX.Element {
  const opts = useMktOptions();
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  return (
    <div className="space-y-2">
      <div className="flex gap-1 justify-end">
        <button type="button" className={`btn-sm ${device === "desktop" ? "btn-secondary" : "btn-ghost"}`} onClick={() => setDevice("desktop")}>Escritorio</button>
        <button type="button" className={`btn-sm ${device === "mobile" ? "btn-secondary" : "btn-ghost"}`} onClick={() => setDevice("mobile")}>Móvil</button>
      </div>
      <FormPreview design={form.design} options={opts.data} device={device} />
    </div>
  );
}

function SubmissionsTab({ form, filter, setFilter }: { form: MktForm; filter: string; setFilter: (f: string) => void }): JSX.Element {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filter]);
  const q = useQuery<{ rows: Submission[]; total: number; pageSize: number }>({
    queryKey: ["mkt-form-subs", form.id, filter, page],
    queryFn: () => api.get(`/mkt/forms/${form.id}/submissions?filter=${filter}&page=${page}`),
  });
  const max = Math.max(form.impressions, form.conversions, 1);
  const steps = [
    { label: "Impresiones", n: form.impressions },
    { label: "Conversiones", n: form.conversions },
    { label: "Contactos nuevos", n: form.contactsCreated },
  ];
  const pages = Math.max(1, Math.ceil((q.data?.total ?? 0) / (q.data?.pageSize ?? 50)));
  return (
    <div className="space-y-5">
      <div className="space-y-1.5" aria-label="Funnel">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-center gap-3 text-sm">
            <div className="w-36 text-ink-secondary shrink-0">{s.label}</div>
            <div className="flex-1 h-6 bg-canvas rounded overflow-hidden">
              <div className="h-full bg-primary/80 rounded" style={{ width: `${Math.max(2, (s.n / max) * 100)}%` }} />
            </div>
            <div className="w-24 text-right tnum text-ink">{s.n}{i > 0 && <span className="text-xs text-ink-muted"> · {pct(s.n, steps[i - 1]!.n)}</span>}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <Select className="w-auto" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los envíos</option><option value="contacts">Solo contactos creados</option><option value="accounts">Solo empresas creadas</option>
        </Select>
        <Button small onClick={() => void downloadCsv(`/mkt/forms/${form.id}/submissions.csv?filter=${filter}`, `envios-${form.name.replace(/[^\w-]+/g, "-").slice(0, 50)}.csv`)}><Download size={14} /> Exportar CSV</Button>
      </div>
      {q.isLoading ? <TableSkeleton rows={4} cols={5} />
        : q.isError ? <ErrorState message="No se pudieron cargar los envíos." onRetry={() => void q.refetch()} />
        : !q.data?.rows.length ? <EmptyState title="Sin envíos" body="Cuando alguien envíe el formulario aparecerá aquí." />
        : (
          <div className="overflow-x-auto scrollbar-thin -mx-4">
            <table className="w-full">
              <thead><tr><th className="th">Fecha</th><th className="th">Contacto</th><th className="th">Datos</th><th className="th">Origen (UTM)</th><th className="th">Estado</th></tr></thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="td whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                    <td className="td">
                      {r.contactId ? <Link className="link" to={`/contacts/${r.contactId}`}>{r.contactName ?? r.contactEmail ?? "Contacto"}</Link> : "—"}
                      {r.contactEmail && <div className="text-xs text-ink-muted">{r.contactEmail}</div>}
                      <div className="flex gap-1 mt-1">{r.contactCreated && <Badge color="blue">Nuevo</Badge>}{r.accountCreated && <Badge color="purple">Empresa nueva</Badge>}</div>
                    </td>
                    <td className="td text-xs max-w-md">
                      {(r.data.summary ?? []).map((s, i) => <div key={i} className="truncate"><span className="text-ink-muted">{s.label}:</span> {s.value}</div>)}
                    </td>
                    <td className="td text-xs">
                      {r.utm ? Object.entries(r.utm).map(([k, v]) => <div key={k} className="truncate max-w-[220px]" title={v}><span className="text-ink-muted">{k.replace("utm_", "")}:</span> {v}</div>) : "—"}
                    </td>
                    <td className="td">{r.status === "pending" ? <Badge color="amber">Por confirmar</Badge> : r.status === "confirmed" ? <Badge color="green">Confirmado</Badge> : <Badge color="green">OK</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {pages > 1 && (
        <div className="flex justify-end items-center gap-2 text-sm">
          <Button small disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="text-ink-muted">Página {page} de {pages}</span>
          <Button small disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
